
import { ValidationObserver } from 'vee-validate';
import scrollIntoView from 'smooth-scroll-into-view-if-needed';
import { formatPhoneAndMask } from '@artmizu/utils';
import { computed, ref } from '@vue/composition-api';
import { nextTick, onMounted, shallowRef, useContext, useRouter, watch } from '@nuxtjs/composition-api';
import type { ProviderInstance } from 'vee-validate/dist/types/types';
import type { Context } from '@nuxt/types';
import type { PartialDeep } from 'type-fest';
import type { ObserverRefVeeValidate } from '~/type/ObserverRefVeeValidate';
import type { CheckoutContact } from '~/type/Checkout';
import { useCartStore } from '~/store/cart';
import type { NewOrder, NewOrderComplete } from '~/type/NewOrder';
import type { CartProduct as CartProductType } from '~/type/product/Product';
import { useUserStore } from '~/store/user';
type RequestType = Awaited<ReturnType<typeof context.$api.order.getOrderData>>;
type veeRefs = Record<string, ProviderInstance>;
const __sfc_main = {};
__sfc_main.setup = (__props, __ctx) => {
  const observerRef = shallowRef<ObserverRefVeeValidate>();
  const formValidationError = ref<{
    key: string;
    value: string;
  }[] | null>(null);
  const localSynced = ref(false);
  const activeIds = ref<number[]>([]);
  const productList = ref<CartProductType[]>([]);
  const userStore = useUserStore();
  const router = useRouter();
  const {
    reset: cartReset
  } = useCartStore();
  const {
    $simpleError,
    $baseError,
    $api
  } = useContext();
  const context = useContext();
  const newOrder = ref<PartialDeep<NewOrder>>({
    contact: {},
    payment: {},
    delivery: {
      address: {
        type: 'courier'
      }
    },
    summary: {
      callbackNeeded: undefined
    }
  });
  const userData = computed(() => userStore.user);
  const avilableProductIDList = computed(() => {
    return productList.value?.filter(p => p.status.code !== 'NOT_AVAILABLE').map(p => p.id);
  });
  const isPrivacyPolicy = ref(true);
  const isPrivacyPolicyChecked = computed(() => isPrivacyPolicy.value);
  const isShowCallbackCheckbox = computed(() => !!(newOrder.value.delivery?.deliveryDate && newOrder.value.delivery?.deliveryTime));
  function setPrivacyPolicy(value: boolean) {
    isPrivacyPolicy.value = value;
  }
  const isAllAvailableProductActive = computed(() => {
    return avilableProductIDList.value?.every(id => {
      return activeIds.value?.includes(id);
    });
  });
  const isAvailableBonusesPay = computed(() => {
    return !productList.value?.find(p => !p.availableBonusPay);
  });
  const isAvailableSetAddress = computed(() => {
    return newOrder.value?.delivery?.address ? !!Object.values(newOrder.value?.delivery.address).find(value => value) : undefined;
  });
  const isPreOrder = computed(() => productList.value?.some(p => p.status.code === 'PRE_ORDER'));
  onMounted(() => {
    window.addEventListener('beforeunload', saveToLocalStorage);
    if (!userData.value) clearLocalStorage();
  });
  watch(() => userData.value, (data?: CheckoutContact) => {
    setUserData(data);
  }, {
    deep: true
  });

  /*
    Только при наличии даты и времени показываем чекбокс подтверждения доставки
   */
  watch([() => newOrder.value.delivery?.deliveryDate, () => newOrder.value.delivery?.deliveryTime], ([date], [time]) => {
    if (newOrder.value.summary) {
      if (date && time && localStorage) newOrder.value.summary.callbackNeeded = JSON.parse(localStorage.getItem('need-callback') || 'true');else newOrder.value.summary.callbackNeeded = undefined;
    }
  });
  setUserData(userData.value);
  function setUserData(data?: CheckoutContact) {
    if (data && newOrder.value?.contact) {
      newOrder.value.contact.name = data.name;
      newOrder.value.contact.phone = data.phone ? formatPhoneAndMask(data.phone) : '';
      newOrder.value.contact.email = data.email;
    }
  }
  function onSuccess({
    response
  }: {
    response: RequestType;
  }) {
    activeIds.value = [];
    productList.value = response?.product;
    response.product?.forEach(p => {
      if (p.isActive && p.status.code !== 'NOT_AVAILABLE') activeIds.value?.push(p.id);
    });

    /**
      * Может быть так, что после обновления корзины кол-во потраченных бонусов,
      * которое выбрано до этого больше кол-ва бонусов, которые пользователь может
      * потратить на оформление этого заказа
     */
    const bonusSpent = Math.min(response.total?.bonus?.available, newOrder?.value?.summary?.bonusSpent || 0);
    if (newOrder.value?.summary) newOrder.value.summary.bonusSpent = bonusSpent;

    /**
      * Если тип еще не выбран - выбираем по дефолту первый и проставляем value для адреса в зависимости от типа
     */
    if (newOrder.value?.delivery && !newOrder.value?.delivery.type) {
      newOrder.value.delivery.type = response?.delivery[0];
      if (newOrder.value?.delivery.type?.allowSelectUserAddress && !newOrder.value?.delivery?.type?.list?.length) newOrder.value.delivery.address = {
        type: 'courier'
      };else if (newOrder.value?.delivery?.type?.list && newOrder.value?.delivery?.type?.list?.length > 0) newOrder.value.delivery.address = newOrder.value?.delivery.type?.list[0];
    } else {
      /**
        * если уже есть выбранный тип, то при обновлении данных у него могла обновиться цена,
        * поэтому берем цену из данных бэка
      */
      const type = response?.delivery?.find(el => el.id === newOrder.value?.delivery?.type?.id);
      if (type && newOrder.value?.delivery) newOrder.value.delivery.type = type;
    }

    /**
      * Устанавливаем value адреса если у выбранного типа доставки, при обновлении данных, есть список адресов
      * и при этом текущий value адреса фактически пустой
    */
    if (newOrder.value?.delivery?.type?.list?.length && !isAvailableSetAddress.value) newOrder.value.delivery.address = newOrder.value?.delivery?.type?.list[0];
    if (!newOrder.value?.payment) newOrder.value.payment = {
      type: response?.payment[0]
    };
    if (process.client && !localSynced.value) {
      // синхронизируемся на следующем тике, чтобы избежать ошибок гидратации
      nextTick(() => {
        restoreDataFromLocal({
          order: response
        });
        localSynced.value = true;
      });
    }
  }
  function onComplete({
    response
  }: {
    response: NewOrderComplete;
  }) {
    clearLocalStorage();
    cartReset({
      ...response.products
    });
    formValidationError.value = null;

    /**
      * для онлайн-оплаты по карте отправляем во внешний экваеринг,
      * иначе отправляем на страницу со статусом заказа
      * 21 - айди онлайн-оплаты
    */
    if (newOrder.value?.payment?.type && newOrder.value?.payment?.type?.id === 21) {
      window.location.replace(response?.paySystemLink);
    } else {
      router.push({
        path: `/order/${response?.hash}/`
      });
    }
  }
  function restoreDataFromLocal({
    order
  }: {
    order: RequestType;
  }) {
    try {
      const data = localStorage ? JSON.parse(localStorage.getItem('checkout-data-v2') || '0') : undefined;
      if (data) {
        for (const prop in data) {
          if (prop === 'payment') {
            const payment = order.payment.find(el => el.id === data[prop]?.type?.id);
            if (payment) newOrder.value.payment = {
              type: payment
            };
          } else if (prop === 'delivery') {
            const type = order.delivery.find(el => el.id === data[prop]?.type?.id);
            if (type) {
              if (type.code === 'PICKUP') {
                const store = type.list?.find(el => {
                  if (el.type === 'pickup') return el.id === data[prop]?.address?.id;
                });
                if (store) {
                  newOrder.value[prop] = {
                    address: store,
                    recipient: data[prop].recipient,
                    recipientPhone: data[prop].recipientPhone,
                    type
                  };
                }
              } else {
                newOrder.value[prop] = {
                  address: data[prop].address,
                  recipient: data[prop].recipient,
                  recipientPhone: data[prop].recipientPhone,
                  type
                };
              }
            }
          } else if (prop === 'contact') {
            newOrder.value[prop] = {
              name: newOrder.value[prop]?.name || data[prop].name,
              phone: newOrder.value[prop]?.phone || data[prop].phone,
              email: newOrder.value[prop]?.email || data[prop].email
            };
          }
        }
      }
    } catch (e) {
      throw new $baseError({
        native: e,
        message: 'Ошибка при парсе данных из локального хранилища'
      });
    }
  }
  function saveToLocalStorage() {
    window.localStorage?.setItem('checkout-data-v2', JSON.stringify({
      contact: newOrder.value?.contact,
      delivery: newOrder.value?.delivery,
      payment: newOrder.value?.payment
    }));
  }
  function clearLocalStorage() {
    window.localStorage?.removeItem('checkout-data-v2');
  }
  function onDataChange<K extends keyof NewOrder>(name: K, data: PartialDeep<NewOrder[K]>) {
    newOrder.value[name] = data;
    /* записываем в локальное хранилище свойство из объекта заказа */
    if (name === 'summary' && 'callbackNeeded' in data) window.localStorage.setItem('need-callback', JSON.stringify(data.callbackNeeded));
  }
  function validate({
    observer = observerRef.value,
    checkInnerObserver = true,
    needScrollToInvalidElement = true,
    boundary,
    onSuccess = () => {},
    onError = () => {}
  }: {
    observer?: ObserverRefVeeValidate;
    checkInnerObserver?: boolean;
    needScrollToInvalidElement?: boolean;
    boundary?: HTMLElement;
    onSuccess?: ({
      observer
    }: {
      observer: ObserverRefVeeValidate;
    }) => void;
    onError?: ({
      validationError,
      observer,
      errors
    }: {
      validationError: {};
      observer: ObserverRefVeeValidate;
      errors: string[];
    }) => void;
  }) {
    observer?.validateWithInfo().then(({
      errors,
      isValid
    }) => {
      const validationError = Object.entries(errors).map(([key, value]) => ({
        key,
        value
      })).filter(error => error.value?.length);
      if (isValid) {
        formValidationError.value = null;
        onSuccess({
          observer
        });
      } else {
        onError({
          validationError,
          observer,
          errors
        });
        formValidationError.value = validationError;
        if (needScrollToInvalidElement) scrollToInvalid({
          validationError,
          observer,
          boundary,
          checkInnerObserver
        });
      }
    });
  }
  function scrollToInvalid({
    validationError,
    observer,
    boundary,
    checkInnerObserver = true
  }: {
    validationError: {
      key: string;
      value: string;
    }[];
    observer: any;
    boundary?: HTMLElement;
    checkInnerObserver: boolean;
  }) {
    let refs: veeRefs = {};
    /**
      * TODO: Не могу понять, какой тип нужно использовать для observer чтобы в нем был массив observers
    */
    if (observer.observers.length && checkInnerObserver) observer?.observers.forEach((obs: any) => refs = {
      ...refs,
      ...obs.refs
    });else refs = {
      ...observer.refs
    };
    const firstInvalidField = refs[validationError[0].key]?.$el;
    scrollTo({
      el: firstInvalidField,
      boundary
    });
  }
  function scrollTo({
    el,
    boundary
  }: {
    el?: Element;
    boundary?: HTMLElement;
  }) {
    if (!el) return;
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        scrollIntoView(el, {
          behavior: 'smooth',
          block: 'center',
          boundary: boundary || null
        });
      });
    });
  }
  async function setActiveList(this: Context, list: number[], {
    onComplete = () => {}
  } = {}) {
    try {
      const self = this;
      await $api.cart.syncActiveProducts.call(self, {
        activeIds: list
      });

      // обновляем список только если запрос выше прошел
      activeIds.value = list;
    } catch (e) {
      new $simpleError({
        native: e,
        message: 'Не удалось переключить активные товары'
      });
    }
    onComplete();
  }
  async function toggleActive(this: Context, id: number, {
    onComplete = () => {}
  } = {}) {
    try {
      const self = this;
      const tmp = [...activeIds.value];
      const index = tmp.findIndex(i => i === id);
      if (index === -1) tmp.push(id);else tmp.splice(index, 1);
      await $api.cart.syncActiveProducts.call(self, {
        activeIds: tmp
      });

      // обновляем список только если запрос выше прошел
      activeIds.value = tmp;
    } catch (e) {
      new $simpleError({
        native: e,
        message: 'Не удалось переключить активные товары'
      });
    }
    onComplete();
  }
  return {
    observerRef,
    formValidationError,
    activeIds,
    newOrder,
    avilableProductIDList,
    isPrivacyPolicyChecked,
    isShowCallbackCheckbox,
    setPrivacyPolicy,
    isAllAvailableProductActive,
    isAvailableBonusesPay,
    isPreOrder,
    onSuccess,
    onComplete,
    onDataChange,
    validate,
    scrollTo,
    setActiveList,
    toggleActive
  };
};
__sfc_main.components = Object.assign({
  ValidationObserver
}, __sfc_main.components);
export default __sfc_main;
